import { getPermission } from '../../../../common/permissions/elementPermissionsUtil';
import { isFeedbackOnlyAccess, isReadOnly } from '../../../../common/permissions/permissionUtil';
import {
    ELEMENTS_DESELECTED,
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_SELECTED,
} from '../../../../common/elements/selectionConstants';
import { getCurrentBoardId } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { getAclIdsSelector } from '../../permissions/permissionsSelector';
import { BATCH_ACTION_TYPE } from '../../../store/reduxBulkingMiddleware';
import { getElements } from '../../../element/selectors/elementsSelector';
import { ELEMENT_DIFF_UPDATE, ELEMENT_UPDATE } from '../../../../common/elements/elementConstants';
import { ElementDiffUpdateAction, ElementUpdateAction } from '../../../../common/elements/elementActionTypes';
import { selectIsElementDeleted } from '../../../element/deletedElements/deletedElementsSelector';
import globalLogger from '../../../logger/logger';
import { prepareActionForLogging } from '../../../../common/logger/loggerUtils';
import { getElement } from '../../../../common/elements/utils/elementTraversalUtils';

const logger = globalLogger.createChannel('socket-client.no-channels');

type SyncAction = {
    type: string;
    payload?: SyncAction[];
};

/**
 * Determines whether the current board permissions make missing live selection channels expected.
 */
const canIgnoreSelectionNoChannelsError = (state: any): boolean => {
    const currentBoardId = getCurrentBoardId(state);
    const elements = getElements(state);
    const currentBoardPermission = getPermission(elements, currentBoardId, getAclIdsSelector(state));

    return isReadOnly(currentBoardPermission) || isFeedbackOnlyAccess(currentBoardPermission);
};

const isDeletedElementMissing = (state: any, elementId: string): boolean => {
    const elements = getElements(state);
    return !getElement(elements, elementId) && selectIsElementDeleted(state, elementId);
};

/**
 * If an element has been deleted then we'd expect that a socket sync channel couldn't be found.
 */
const canIgnoreElementUpdateNoChannelsError = (state: any, action: ElementUpdateAction): boolean => {
    const canIgnore =
        !action.updates.length || action.updates.every((update) => isDeletedElementMissing(state, update.id));

    // We think we've handled cases where this might happen already, so logging a warning if it does
    if (canIgnore) {
        logger.warn('Ignoring no-channel error for deleted element update', prepareActionForLogging(action));
    }

    return canIgnore;
};

/**
 * If an element has been deleted then we'd expect that a socket sync channel couldn't be found.
 */
const canIgnoreElementDiffUpdateNoChannelsError = (state: any, action: ElementDiffUpdateAction): boolean => {
    const canIgnore = isDeletedElementMissing(state, action.id);

    // We think we've handled cases where this might happen already, so logging a warning if it does
    if (canIgnore) {
        logger.warn('Ignoring no-channel error for deleted element diff update', prepareActionForLogging(action));
    }

    return canIgnore;
};

/**
 * Determines whether an action failing to find socket channels is expected for the given action.
 */
export const canIgnoreNoChannelsError = (state: any, syncAction: SyncAction): boolean => {
    switch (syncAction.type) {
        case BATCH_ACTION_TYPE: {
            const actions = syncAction.payload || [];
            return !!actions.length && actions.every((action) => canIgnoreNoChannelsError(state, action));
        }
        case ELEMENT_UPDATE:
            return canIgnoreElementUpdateNoChannelsError(state, syncAction as ElementUpdateAction);
        case ELEMENT_DIFF_UPDATE:
            return canIgnoreElementDiffUpdateNoChannelsError(state, syncAction as ElementDiffUpdateAction);
        case ELEMENTS_SELECTED:
        case ELEMENTS_DESELECTED:
        case ELEMENTS_DESELECT_ALL:
            return canIgnoreSelectionNoChannelsError(state);
        default:
            return false;
    }
};
